/**
 * PartnerOverview – Dashboard-Sicht für aktive Affiliate-Partner.
 *
 * Inhalt:
 *  1) Hero-Erklärung in 3 Schritten (für Erzieherinnen verständlich)
 *  2) Rabattcode-Karte (FREUNDE10) mit Copy-Button und Beispieltext
 *  3) KPI-Übersicht (Klicks, Conversions, Provisionen)
 *  4) Fertige Produkt-Links inkl. ?ref=… und ?coupon=FREUNDE10 zum Teilen
 *  5) FAQ-Karte mit den häufigsten Fragen
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import {
  Loader2,
  MousePointerClick,
  Users,
  Hourglass,
  CheckCircle2,
  Banknote,
  Link2,
  ExternalLink,
  Copy,
  Check,
  Share2,
  Tag,
} from "lucide-react";
import PartnerHeroSteps from "./PartnerHeroSteps";
import PartnerFaq from "./PartnerFaq";

const DISCOUNT_CODE = "FREUNDE10";
const BASE_URL = "https://www.musikonzept.de";

const formatEur = (cents: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format((cents || 0) / 100);

interface Kpi {
  clicks: number;
  conversions: number;
  pending: number;
  approved: number;
  paid: number;
}

const PRODUCT_LINKS: Array<{ label: string; description: string; path: string }> = [
  {
    label: "Mitgliedschaft (Monatlich)",
    description: "Für Fachkräfte, die flexibel starten wollen.",
    path: "/abo-checkout/pro?interval=monthly",
  },
  {
    label: "Mitgliedschaft (Jährlich)",
    description: "Beliebt: günstiger als monatlich, perfekt für ganze Teams.",
    path: "/abo-checkout/pro?interval=yearly",
  },
  {
    label: "Ausbildung Fachkraft Musik (330h)",
    description: "Staatlich zugelassener Fernlehrgang (ZFU) – ideal für deine pädagogischen Kontakte.",
    path: "/ausbildung/fachkraft-musik",
  },
  {
    label: "Ausbildung Fachkraft Gitarre",
    description: "Für alle, die Gitarre in der Kita oder Schule einsetzen möchten.",
    path: "/ausbildung/fachkraft-gitarre",
  },
  {
    label: "Alle Termine & Webinare",
    description: "Einzelbuchungen – auch hier bekommst du Provision.",
    path: "/termine",
  },
];

const buildLink = (path: string, code: string) => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(cleanPath, BASE_URL);
  url.searchParams.set("ref", code);
  url.searchParams.set("coupon", DISCOUNT_CODE);
  return url.toString();
};

const CopyButton = ({ value, label = "Kopieren" }: { value: string; label?: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast({ title: "In die Zwischenablage kopiert" });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: "Kopieren fehlgeschlagen", variant: "destructive" });
    }
  };
  return (
    <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
      {copied ? <Check className="mr-2 h-4 w-4 text-primary" /> : <Copy className="mr-2 h-4 w-4" />}
      {copied ? "Kopiert" : label}
    </Button>
  );
};

const DiscountCodeCard = ({ code }: { code: string }) => (
  <Card>
    <CardHeader>
      <div className="flex items-center gap-2">
        <Tag className="h-5 w-5 text-primary" />
        <CardTitle>Dein Rabattcode zum Teilen</CardTitle>
      </div>
      <CardDescription>
        Diesen Code können deine Kontakte beim Checkout eingeben – egal welches Produkt.
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border-2 border-dashed border-primary/40 bg-primary/5 p-4">
        <Badge variant="secondary" className="px-3 py-1 font-mono text-2xl tracking-widest">
          {DISCOUNT_CODE}
        </Badge>
        <span className="text-sm text-muted-foreground">= 10% Rabatt auf alles, dauerhaft</span>
        <div className="ml-auto">
          <CopyButton value={DISCOUNT_CODE} label="Code kopieren" />
        </div>
      </div>
      <div className="rounded-md bg-muted/50 p-3 text-sm">
        <span className="font-medium">Beispiel für deine Nachricht:</span>
        <p className="mt-1 italic text-muted-foreground">
          „Schau mal bei MusiKonzept vorbei – mit dem Code <strong>{DISCOUNT_CODE}</strong> bekommst du 10% Rabatt auf
          jede Mitgliedschaft, Ausbildung oder Webinar-Anmeldung. Dein Code, dein Vorteil – und für mich gibt's eine
          kleine Provision."
        </p>
      </div>
      <div className="text-xs text-muted-foreground">
        Dein persönlicher Tracking-Code: <span className="font-mono font-semibold text-foreground">{code}</span> – wird
        in deinen Links automatisch mitgesendet.
      </div>
    </CardContent>
  </Card>
);

const KpiGrid = ({ kpi, isLoading }: { kpi: Kpi | undefined; isLoading: boolean }) => {
  const cards = [
    { icon: MousePointerClick, label: "Klicks", value: kpi ? String(kpi.clicks) : "–" },
    { icon: Users, label: "Conversions", value: kpi ? String(kpi.conversions) : "–" },
    { icon: Hourglass, label: "Offen", value: kpi ? formatEur(kpi.pending) : "–" },
    { icon: CheckCircle2, label: "Auszahlbar", value: kpi ? formatEur(kpi.approved) : "–" },
    { icon: Banknote, label: "Bezahlt", value: kpi ? formatEur(kpi.paid) : "–" },
  ];
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {cards.map((c) => (
        <Card key={c.label}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{c.label}</CardTitle>
            <c.icon className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{c.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const ProductLinksCard = ({ code }: { code: string }) => (
  <Card>
    <CardHeader>
      <div className="flex items-center gap-2">
        <Share2 className="h-5 w-5 text-primary" />
        <CardTitle>Deine fertigen Empfehlungs-Links</CardTitle>
      </div>
      <CardDescription>
        Jeder Link enthält bereits deinen Tracking-Code und den Rabattcode {DISCOUNT_CODE}. Einfach kopieren und
        verschicken.
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      {PRODUCT_LINKS.map((p) => {
        const link = buildLink(p.path, code);
        return (
          <div key={p.path} className="rounded-lg border p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="font-medium">{p.label}</div>
                <div className="text-xs text-muted-foreground">{p.description}</div>
              </div>
              <CopyButton value={link} label="Link kopieren" />
            </div>
            <Input readOnly value={link} className="mt-2 font-mono text-xs" />
          </div>
        );
      })}
    </CardContent>
  </Card>
);

interface Props {
  affiliateId: string;
  code: string;
}

const PartnerOverview = ({ affiliateId, code }: Props) => {
  const { data: kpi, isLoading } = useQuery({
    queryKey: ["student-partner-kpi", affiliateId],
    queryFn: async (): Promise<Kpi> => {
      const [clicksRes, refRes, commRes] = await Promise.all([
        supabase.from("affiliate_clicks").select("id", { count: "exact", head: true }).eq("affiliate_id", affiliateId),
        supabase
          .from("affiliate_referrals")
          .select("id", { count: "exact", head: true })
          .eq("affiliate_id", affiliateId),
        supabase.from("affiliate_commissions").select("status, commission_amount").eq("affiliate_id", affiliateId),
      ]);
      const rows = (commRes.data ?? []) as Array<{ status: string; commission_amount: number | null }>;
      const sumBy = (s: string) =>
        rows.filter((r) => r.status === s).reduce((acc, r) => acc + Number(r.commission_amount ?? 0), 0);
      return {
        clicks: clicksRes.count ?? 0,
        conversions: refRes.count ?? 0,
        pending: sumBy("pending"),
        approved: sumBy("approved"),
        paid: sumBy("paid"),
      };
    },
    staleTime: 30_000,
  });

  return (
    <div className="container mx-auto max-w-6xl space-y-6 px-4 py-8 md:py-10">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Partnerprogramm</h1>
          <p className="mt-1 text-muted-foreground">Empfehle MusiKonzept und verdiene 20% Provision.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/partner/links">
              <Link2 className="mr-2 h-4 w-4" /> Alle Links
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/partner/auszahlungen">
              <Banknote className="mr-2 h-4 w-4" /> Auszahlungen
            </Link>
          </Button>
          <Button asChild>
            <Link to="/partner">
              <ExternalLink className="mr-2 h-4 w-4" /> Vollansicht
            </Link>
          </Button>
        </div>
      </div>

      <PartnerHeroSteps />
      <DiscountCodeCard code={code} />
      <KpiGrid kpi={kpi} isLoading={isLoading} />
      <ProductLinksCard code={code} />
      <PartnerFaq />
    </div>
  );
};

export default PartnerOverview;
